// The clinic's clock. Appointment times, "today" and date navigation follow Phoenix
// time whatever time zone the viewing browser is set to. Phoenix has no DST.

export const PHOENIX_TIME_ZONE = 'America/Phoenix';

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const CLOCK_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: PHOENIX_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** The Phoenix wall-clock fields of an instant: { year, month (1-12), day, hours (0-23), minutes }. */
export const phoenixClock = (value) => {
  const date = toDate(value);
  if (!date) return null;
  const parts = Object.fromEntries(
    CLOCK_FORMAT.formatToParts(date).map(({ type, value: part }) => [type, Number(part)]),
  );
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hours: parts.hour % 24,
    minutes: parts.minute,
  };
};

/** Today's date in Phoenix as YYYY-MM-DD. */
export const phoenixDateString = (value = new Date()) => {
  const clock = phoenixClock(value);
  if (!clock) return '';
  return `${clock.year}-${String(clock.month).padStart(2, '0')}-${String(clock.day).padStart(2, '0')}`;
};

/**
 * Local midnight of today's Phoenix date. Date pickers and navigation work with local
 * Date objects; this one has the Phoenix calendar date in its local fields.
 */
export const phoenixToday = (value = new Date()) => {
  const clock = phoenixClock(value);
  return clock ? new Date(clock.year, clock.month - 1, clock.day) : null;
};

/** An instant as a Phoenix time of day, e.g. "9:30 AM". `options` override the format. */
export const formatPhoenixTime = (value, options = {}) => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options,
    timeZone: PHOENIX_TIME_ZONE,
  });
};

/** An instant as a 24-hour Phoenix time, e.g. "09:30" or "17:05". */
export const formatPhoenixTime24 = (value) => {
  const clock = phoenixClock(value);
  if (!clock) return '';
  return `${String(clock.hours).padStart(2, '0')}:${String(clock.minutes).padStart(2, '0')}`;
};

/**
 * An instant as a treatment's stored time on Phoenix clocks, "hh:mm AM" (e.g. "09:30 AM",
 * "05:00 PM"), whatever time zone the browser is set to.
 */
export const formatPhoenixOrderTime = (value) => {
  const clock = phoenixClock(value);
  if (!clock) return '';
  const hours12 = clock.hours % 12 || 12;
  const meridiem = clock.hours < 12 ? 'AM' : 'PM';
  return `${String(hours12).padStart(2, '0')}:${String(clock.minutes).padStart(2, '0')} ${meridiem}`;
};

/**
 * A local Date whose fields show an instant's Phoenix wall-clock time, for time pickers
 * that work in the browser's zone: the picked time is saved as a Phoenix time.
 */
export const phoenixWallClockDate = (value) => {
  const clock = phoenixClock(value);
  return clock ? new Date(clock.year, clock.month - 1, clock.day, clock.hours, clock.minutes) : null;
};

/** An instant's Phoenix date as a treatment's stored date, "YYYY-MM-DDT00:00:00.000Z". */
export const phoenixOrderDate = (value = new Date()) => {
  const date = phoenixDateString(value);
  return date ? `${date}T00:00:00.000Z` : '';
};

/**
 * Minutes past midnight of a clock time such as "9:30 AM", "05:00 PM" or "17:05",
 * for sorting. null when the text isn't a time.
 */
export const clockTimeToMinutes = (text) => {
  const match = /^\s*(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?\s*$/i.exec(String(text ?? '').replace(/[  ]/g, ' '));
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.[0]?.toLowerCase();
  if (minutes > 59 || hours > (meridiem ? 12 : 23)) return null;
  if (meridiem === 'p' && hours !== 12) hours += 12;
  if (meridiem === 'a' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};
